import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { translateAuthError } from "@/components/auth/AuthErrorTranslations";

const MIN_PASSWORD_LENGTH = 8;

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsSessionReady(true);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setIsSessionReady(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const passwordsMatch = newPassword === confirmPassword;
  const isPasswordValid = newPassword.length >= MIN_PASSWORD_LENGTH;
  const isFormValid = isPasswordValid && passwordsMatch && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) {
        toast({
          title: "Błąd",
          description: translateAuthError(error.message),
          variant: "destructive",
        });
        return;
      }

      setIsSuccess(true);
      toast({
        title: "Hasło zmienione",
        description: "Twoje hasło zostało pomyślnie zaktualizowane.",
      });

      setTimeout(() => navigate("/panel", { replace: true }), 2000);
    } catch {
      toast({
        title: "Błąd",
        description: "Wystąpił nieoczekiwany błąd",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] flex flex-col items-center justify-center px-4 py-8">
      <div className="text-center mb-8">
        <button
          onClick={() => navigate("/")}
          className="text-3xl font-bold text-primary tracking-tight hover:opacity-80 transition-opacity"
        >
          Viraluj.pl
        </button>
        <p className="text-muted-foreground mt-1 text-sm">
          {isSuccess ? "Gotowe! Przekierowujemy Cię..." : "Ustaw nowe hasło do swojego konta"}
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-[440px]"
      >
        <div className="bg-card rounded-2xl shadow-[var(--shadow-card)] border border-border/50 p-6 sm:p-8">
          {isSuccess ? (
            <div className="text-center space-y-4 py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Hasło zmienione!</h3>
              <p className="text-sm text-muted-foreground">
                Za chwilę zostaniesz przekierowany do panelu.
              </p>
            </div>
          ) : !isSessionReady ? (
            <div className="text-center space-y-4 py-4">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
              <p className="text-sm text-muted-foreground">
                Weryfikacja tokenu resetowania...
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mb-4">
                  <Lock className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Nowe hasło</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Wprowadź nowe hasło (min. {MIN_PASSWORD_LENGTH} znaków)
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nowe hasło</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Wprowadź nowe hasło"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={MIN_PASSWORD_LENGTH}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {newPassword.length > 0 && !isPasswordValid && (
                    <p className="text-xs text-destructive">
                      Hasło musi mieć co najmniej {MIN_PASSWORD_LENGTH} znaków
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Powtórz hasło</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Powtórz nowe hasło"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="h-11"
                  />
                  {confirmPassword.length > 0 && !passwordsMatch && (
                    <p className="text-xs text-destructive">Hasła nie są takie same</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl font-medium"
                  disabled={isLoading || !isFormValid}
                >
                  {isLoading ? "Zapisywanie..." : "Zapisz nowe hasło"}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => navigate("/auth")}
                >
                  Powrót do logowania
                </Button>
              </form>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
